import React, { useCallback, useEffect, useState } from 'react'
import { CBadge, CSmartTable } from '@coreui/react-pro'
import moment from 'moment'

import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import UserService from 'src/service/UserService'

const SEARCH_FIELDS = [
  { key: 'full_name', label: 'Member' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'id', label: 'Member ID' },
]

const columns = [
  { label: 'Member ID', key: 'id', filter: false, sorter: true, _style: { width: '10%' } },
  { label: 'Member', key: 'full_name', filter: false, sorter: true },
  { label: 'Email', key: 'email', filter: false, sorter: true },
  { label: 'Phone', key: 'phone', filter: false, sorter: false },
  { label: 'Registered', key: 'created_at', filter: false, sorter: true },
  { label: 'Status', key: 'is_active', filter: false, sorter: false },
]

const Table = ({ onTotalChange }) => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('full_name')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState()

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const fetchData = useCallback(() => {
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    setLoading(true)
    UserService.getUser(offset, itemsPerPage, activePage, params)
      .then((result) => {
        setUsers(result.data)
        onTotalChange?.(result.data?.total ?? 0)
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
      })
  }, [activePage, columnFilter, columnSorter, itemsPerPage, onTotalChange])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <div className="um-panel-heading">
        <div>
          <h2 className="um-panel-title">Members List</h2>
          <p className="um-panel-note">Everyone registered in this game.</p>
        </div>
      </div>

      <TableSearchToolbar
        fields={SEARCH_FIELDS}
        field={searchField}
        onFieldChange={handleFieldChange}
        value={columnFilter?.[searchField] || ''}
        onSearch={applySearch}
        placeholder={`Search by ${activeSearchLabel}...`}
      />

      <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
        <CSmartTable
          columns={columns}
          columnSorter={{ external: true }}
          noItemsLabel="No members found"
          scopedColumns={{
            id: (item) => <td className="um-muted">{item.id}</td>,
            full_name: (item) => <td className="fw-semibold">{item.full_name}</td>,
            phone: (item) => <td>{item.phone || '-'}</td>,
            created_at: (item) => (
              <td>{item.created_at ? moment(item.created_at).format('D.MM.YYYY') : '-'}</td>
            ),
            is_active: (item) => (
              <td>
                <CBadge
                  color={item.is_verified === 1 ? 'success' : 'secondary'}
                  shape="rounded-pill"
                  className="px-2 py-1"
                >
                  {item.is_verified === 1 ? 'Verified' : 'Unverified'}
                </CBadge>
              </td>
            ),
          }}
          items={users?.data}
          itemsPerPage={itemsPerPage}
          itemsPerPageSelect
          loading={loading}
          pagination={{ external: true }}
          paginationProps={{
            activePage,
            pages: Math.ceil(users?.total / itemsPerPage) || 1,
          }}
          tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          onActivePageChange={(page) => setActivePage(page)}
          onItemsPerPageChange={(perPage) => {
            setActivePage(1)
            setItemsPerPage(perPage)
          }}
          onSorterChange={(sorter) => setColumnSorter(sorter)}
        />
      </div>
    </>
  )
}

export default Table
